import React, { useEffect, useState } from "react";
import PropTypes from "prop-types";
import { useParams } from "react-router-dom";

import {
  Paper,
  Table,
  TableBody,
  TableRow,
  TableCell,
  TableContainer,
  TextField,
  Typography,
  Grid,
  Button,
} from "@material-ui/core";
import { fetchDataFromApi } from "utils";
import LoadingOverlay from "react-loading-overlay";
import LoadingIcon from "components/LoadingIcon/LoadingIcon";
import eightAxios from "configs/axiosConfig";
import { useNotifyContext } from "components/Notify";
import { useTranslation } from "react-i18next";
import Modal from "components/Modal";

let firstColumn = [
  { id: 1, name: "Employee ID", apiKey: "employee_id" },
  { id: 2, name: "Employee Name", apiKey: "name" },
  { id: 3, name: "App Profile", apiKey: "role" },
  { id: 4, name: "Nationality", apiKey: "nationality" },
  { id: 5, name: "ID Type", apiKey: "id_type" },
  { id: 6, name: "Identification Number", apiKey: "id_number" },
  { id: 7, name: "Mobile No.", apiKey: "mobile_number" },
  { id: 8, name: "Email Address", apiKey: "email" },
  { id: 9, name: "Residental Address", apiKey: "residential_address" },
  { id: 10, name: "Employment Status", apiKey: "employment_status" },
  { id: 11, name: "App User ID Status", apiKey: "status" },
];
let secondColumn = [
  { id: 1, name: "Worker Group", apiKey: "worker_group" },
  { id: 2, name: "Zone", apiKey: "zone" },
  { id: 3, name: "Region", apiKey: "region" },
  { id: 4, name: "Estate Name", apiKey: "estate" },
  { id: 5, name: "Division Name", apiKey: "division" },
  { id: 6, name: "Block Name", apiKey: "block" },
];
let thirdColumn = [
  { id: 1, name: "Created Date/Time", apiKey: "created_at" },
  { id: 2, name: "Created By", apiKey: "created_by" },
  { id: 3, name: "Approved/Rejected By", apiKey: "approved_rejected_by" },
  {
    id: 4,
    name: "Approved/Rejected Date/Time",
    apiKey: "approved_rejected_date",
  },

  { id: 5, name: "Remarks", apiKey: "remarks" },
  { id: 6, name: "Status", apiKey: "status" },
];
const getUserDetailUrl = (id) => {
  return `/new-user/${id}/view-profile/`;
};

const RemarkBody = ({ setRemarks, errorMessage }) => {
  const remarksHandler = (event) => {
    event.preventDefault();
    setRemarks(event.target.value);
  };
  return (
    <Grid container direction="column" spacing={2} style={{ padding: "10px" }}>
      <Grid item>
        <TextField
          variant="outlined"
          name="description"
          fullWidth
          multiline
          rows={3}
          inputProps={{ maxLength: 50 }}
          onChange={remarksHandler}
        />
        <Grid container justify="center">
          {errorMessage ? (
            <p style={{ textAlign: "center", color: "red" }}>{errorMessage}</p>
          ) : null}
        </Grid>
      </Grid>
    </Grid>
  );
};
RemarkBody.propTypes = {
  setRemarks: PropTypes.func,
  errorMessage: PropTypes.string,
};

const UserProfile = () => {
  const { t } = useTranslation();
  const [userDetail, setUserDetail] = useState(null);
  const [remarks, setRemarks] = useState("");
  const [errorMessage, setErrorMessage] = useState("");
  const [auditColumn, setAuditColumn] = useState(thirdColumn);
  const [modalFields, setModalFields] = useState({
    id: null,
    title: "Add Remarks",
    isOpen: false,
    body: null,
    confirmButtonLabel: "Reject User",
    onConfirm: null,
    resetModalBodyState: () => setErrorMessage(""),
  });
  const params = useParams();
  const setNotify = useNotifyContext();

  useEffect(() => {
    if (params.id && !userDetail) {
      fetchDataFromApi(getUserDetailUrl(params.id), setUserDetail);
    }
  }, [userDetail]);

  useEffect(() => {
    if (userDetail && typeof userDetail["status"] === "string") {
      if (userDetail["status"].toLowerCase() === "pending") {
        setAuditColumn(
          thirdColumn.filter((column) => {
            return ![3, 4, 5].includes(column.id);
          })
        );
      } else if (userDetail["status"].toLowerCase() === "active") {
        setAuditColumn(
          thirdColumn.filter((column) => {
            return ![5].includes(column.id);
          })
        );
      }
    }
  }, [userDetail]);

  useEffect(() => {
    setModalFields({
      ...modalFields,
      body: <RemarkBody errorMessage={errorMessage} setRemarks={setRemarks} />,
      onConfirm: () => {
        eightAxios
          .patch(urls.declineURL.replace("{id}", modalFields.id), {
            remarks: remarks,
          })
          .then((response) => {
            setModalFields({ ...modalFields, isOpen: false });
            setNotify({
              isOpen: true,
              message: response.data.message,
              type: "success",
            });
            setUserDetail(null);
            setAuditColumn(thirdColumn);
          })
          .catch((error) => {
            if (error.response) {
              let errMsg = "";
              for (let key in error.response.data) {
                errMsg += error.response.data[key] + "\n";
              }
              setErrorMessage(errMsg);
              setNotify({
                isOpen: true,
                message: "User decline failed. Please try again.",
                type: "error",
              });
            } else {
              setErrorMessage(error.message);
              setNotify({
                isOpen: true,
                message: "User decline failed. Please try again.",
                type: "error",
              });
            }
          });
      },
    });
  }, [modalFields.id, errorMessage, remarks]);

  const urls = {
    proxyURL: "new-user/pending-users/",
    exportURL: "new-user/pending-users/export/",
    approveURL: "new-user/{id}/approve-pending-user/",
    declineURL: "new-user/{id}/decline-pending-user/",
  };

  const approveUser = (event) => {
    event.preventDefault();
    eightAxios
      .patch(urls.approveURL.replace("{id}", params.id))
      .then((response) => {
        setNotify({
          isOpen: true,
          message: response.data.message,
          type: "success",
        });
        setUserDetail(null);
        setAuditColumn(thirdColumn);
      })
      .catch(() => {
        setNotify({
          isOpen: true,
          message: "User approve failed. Please try again.",
          type: "error",
        });
      });
  };

  const declineUser = (event) => {
    event.preventDefault();
    setModalFields({
      ...modalFields,
      id: params.id,
      isOpen: true,
    });
  };

  // if (redirect && redirectUrl) {
  //   return <Redirect to={redirectUrl} />;
  // }

  return (
    <LoadingOverlay
      styles={{
        overlay: (base) => ({
          ...base,
          background: "rgba(0, 0, 0, 0.2)",
        }),
      }}
      active={!userDetail}
      spinner={<LoadingIcon />}
    >
      <Paper>
        <Grid container style={{ padding: "20px" }}>
          <Grid item xs={12} sm={12} md={6} lg={6} style={{ padding: "20px" }}>
            <Typography variant="button" color="textPrimary">
              {t("Personal Details")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {firstColumn.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{
                          backgroundColor: "#d9d9d9",
                          width: "35%",
                          fontWeight: "bold",
                          border: "2px solid rgba(0, 0, 0, 0.9)",
                        }}
                      >
                        {data.name}
                      </TableCell>
                      <TableCell
                        style={{ border: "2px solid rgba(0, 0, 0, 0.9)" }}
                      >
                        {userDetail ? userDetail[data.apiKey] : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <br></br>
            <Typography variant="button" color="textPrimary">
              {t("Employment Details")}
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {secondColumn.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{
                          backgroundColor: "#d9d9d9",
                          width: "35%",
                          fontWeight: "bold",
                          border: "2px solid rgba(0, 0, 0, 0.9)",
                        }}
                      >
                        {data.name}
                      </TableCell>
                      <TableCell
                        style={{ border: "2px solid rgba(0, 0, 0, 0.9)" }}
                      >
                        {userDetail ? userDetail[data.apiKey] : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
            <br></br>
            <Typography variant="button" color="textPrimary">
              Audit Details
            </Typography>
            <TableContainer>
              <Table size="small">
                <TableBody>
                  {auditColumn.map((data, index) => (
                    <TableRow key={index}>
                      <TableCell
                        style={{
                          backgroundColor: "#d9d9d9",
                          width: "35%",
                          fontWeight: "bold",
                          border: "2px solid rgba(0, 0, 0, 0.9)",
                        }}
                      >
                        {data.name}
                      </TableCell>
                      <TableCell
                        style={{ border: "2px solid rgba(0, 0, 0, 0.9)" }}
                      >
                        {userDetail ? userDetail[data.apiKey] : null}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          </Grid>

          <Grid item xs={12} sm={12} md={6} lg={6} style={{ padding: "20px" }}>
            <Grid
              container
              spacing={5}
              direction="column"
              alignItems="center"
              justify="center"
            >
              <Grid item>
                <Typography align="center" variant="h6" color="textPrimary">
                  {userDetail ? `Name: ${userDetail.name}` : null}
                </Typography>
              </Grid>
              <Grid item>
                {userDetail ? (
                  userDetail.qr_code ? (
                    <img src={userDetail.qr_code} alt="qrcode" width="300px" />
                  ) : (
                    <Typography align="center" variant="h6" color="textPrimary">
                      QR CODE NOT GENERATED
                    </Typography>
                  )
                ) : null}
              </Grid>
            </Grid>
          </Grid>
        </Grid>

        {userDetail &&
        userDetail["status"].toLowerCase() === "pending" &&
        params.id ? (
          <Grid container justify="center">
            <Grid item sm={12} md={6}>
              <Grid container justify="space-evenly" spacing={2}>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={approveUser}
                  >
                    Approve
                  </Button>
                </Grid>
                <Grid item xs={4}>
                  <Button
                    variant="contained"
                    color="primary"
                    fullWidth
                    onClick={declineUser}
                  >
                    Reject
                  </Button>
                </Grid>
              </Grid>
            </Grid>
            <Grid item sm={12} md={6}></Grid>
          </Grid>
        ) : null}
        <br></br>
      </Paper>
      <Modal modalFields={modalFields} setModalFields={setModalFields} />
    </LoadingOverlay>
  );
};

UserProfile.propTypes = {
  profileType: PropTypes.oneOf(["normal", "pending", "rejected", "inactive"]),
  redirectUrl: PropTypes.string,
};

export default UserProfile;
